import { useCallback, useEffect, useState } from 'react';
import { findIntegration, type Integration } from '@lapo/core';
import type { ToastInput } from '@lapo/ui';
import type { ApiClient } from './auth/apiClient.js';
import type { AuthSession } from './auth/provider.js';
import { API_URL } from './config.js';

/**
 * Owns the Integrations HUB: the per-integration master on/off enablement map (which gates
 * integration-owned side-menu items), the set of cards whose run buttons are spinning, and the
 * actions the cards grid + detail drive — run a card (`api.runIntegration` → splice the refreshed
 * row + toast), toggle a card's master switch (`api.setIntegrationEnabled`, optimistic + revert),
 * and the two loaders that hydrate the enablement map (`api.getIntegrationEnablementMap`) and the
 * live hub list (`api.listIntegrations`) on sign-in / connection changes.
 *
 * Behavior-preserving extraction from App.tsx. Two things stay in App by necessity, both a lexical
 * ordering constraint, not a design choice:
 *
 *  1. `hubIntegrations` / `setHubIntegrations` themselves. The live-list refetch effect is keyed on
 *     the set of linked service accounts (todoist/easypost/granola/authorizedApps/slackWorkspaces),
 *     and `slackWorkspaces` is only produced by a Slack config hook that ITSELF needs
 *     `setHubIntegrations` (its "Sync now" splices the refreshed row). So the hub-list state must be
 *     declared BEFORE that Slack hook, while its connection-keyed refetch must run AFTER it. App
 *     therefore keeps `const [hubIntegrations, setHubIntegrations] = useState(INTEGRATIONS)` early
 *     (so `useFinancial` + every config hook can splice into it) and passes both into this hook,
 *     which is called once all connection keys are in scope. `connectionKeys` reproduces App's
 *     original effect dep array exactly.
 *
 *  2. The cross-cluster refresh `onRunIntegration` fires AFTER a successful run — Contact Pictures
 *     writes photos onto contacts, so a `contact-pictures` run refreshes the directory (App's
 *     `loadContacts`, in the Contacts cluster). App supplies it as `onAfterRun(id)`, invoked at the
 *     exact point the inline code called `loadContacts()` (inside the run success handler, after the
 *     splice + toast). App reproduces the same `id === 'contact-pictures'` mapping there.
 *
 * The nav-enabled derivations (subscription/financial/tesseta/clavero/bills) stay in App: they read
 * this hook's `integrationEnablement` alongside config state from several OTHER clusters. `session`
 * null (signed out) keeps everything on the offline fallback catalog / empty enablement map.
 */
export interface UseIntegrationsHubDeps {
  api: ApiClient;
  session: AuthSession | null;
  pushToast: (t: ToastInput) => number;
  /** App-owned hub-list state (kept early so the config hooks + useFinancial can splice into it). */
  hubIntegrations: Integration[];
  setHubIntegrations: React.Dispatch<React.SetStateAction<Integration[]>>;
  /**
   * Extra deps that gate the live-list refetch: App passes the linked service-account ids +
   * authorized-app / workspace counts so a connect/disconnect from a hub panel refires the fetch
   * (keeps card/detail status badges in sync). Reproduces App's original effect dep array.
   */
  connectionKeys: ReadonlyArray<unknown>;
  /**
   * Fired after a card run succeeds (post splice + toast). App reproduces the exact cross-cluster
   * mapping — a `contact-pictures` run refreshes the contact directory (`loadContacts`).
   */
  onAfterRun: (id: string) => void;
}

export interface UseIntegrationsHub {
  integrationEnablement: Record<string, boolean> | null;
  setIntegrationEnablement: React.Dispatch<React.SetStateAction<Record<string, boolean> | null>>;
  runningIntegrations: Set<string>;
  onRunIntegration: (id: string) => void;
  onToggleIntegration: (id: string, enabled: boolean) => void;
}

export function useIntegrationsHub({
  api,
  session,
  pushToast,
  hubIntegrations,
  setHubIntegrations,
  connectionKeys,
  onAfterRun,
}: UseIntegrationsHubDeps): UseIntegrationsHub {
  // Per-integration master on/off switches (absent id → enabled). Loaded on sign-in so integration-owned
  // side-menu items can be hidden while suspended; the hub detail's toggle updates it (below).
  const [integrationEnablement, setIntegrationEnablement] = useState<Record<string, boolean> | null>(null);
  useEffect(() => {
    if (!session || !API_URL) return;
    let cancelled = false;
    void api
      .getIntegrationEnablementMap()
      .then((map) => {
        if (!cancelled) setIntegrationEnablement(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [session, api]);

  // `runningIntegrations` tracks which cards' run buttons are spinning.
  const [runningIntegrations, setRunningIntegrations] = useState<Set<string>>(() => new Set());
  // Fetch the live list once signed in; leaves the fallback catalog in place on any error (offline).
  // Also refetch whenever the set of linked service accounts changes (connect/disconnect from a hub
  // panel), so the card/detail status badges stay in sync with the connection panels — otherwise a
  // just-connected integration would keep showing the offline "Not connected" fallback until reload.
  useEffect(() => {
    if (!session || !API_URL) return;
    let cancelled = false;
    void api
      .listIntegrations()
      .then((list) => {
        if (!cancelled && list.length) setHubIntegrations(list);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, api, ...connectionKeys]);

  const onRunIntegration = useCallback(
    (id: string) => {
      const integration = findIntegration(hubIntegrations, id);
      if (!integration || !integration.runnable) return;
      setRunningIntegrations((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      const clear = (): void =>
        setRunningIntegrations((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      void api
        .runIntegration(id)
        .then((updated) => {
          // Splice the refreshed integration (new status + audit) back into the list.
          setHubIntegrations((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
          pushToast({ tone: 'success', message: `${integration.name} ran.` });
          // Contact Pictures writes photos onto contacts — refresh the directory so they show.
          // (App owns the mapping since it reaches the Contacts cluster's `loadContacts`.)
          onAfterRun(id);
        })
        .catch(() => pushToast({ tone: 'error', message: `${integration.name} run failed.` }))
        .finally(clear);
    },
    [api, hubIntegrations, onAfterRun, pushToast, setHubIntegrations],
  );

  // Flip an integration's master on/off switch. Optimistic: update the card immediately, persist, and
  // splice the refreshed integration back in (or revert on failure). Suspending keeps all config; it
  // only pauses runs and hides the integration's own side-menu items (derived from `enabled` below).
  const onToggleIntegration = useCallback(
    (id: string, enabled: boolean) => {
      const integration = findIntegration(hubIntegrations, id);
      if (!integration) return;
      // Optimistically update both the hub card/detail and the nav-gating map.
      setHubIntegrations((prev) => prev.map((i) => (i.id === id ? { ...i, enabled } : i)));
      setIntegrationEnablement((m) => ({ ...(m ?? {}), [id]: enabled }));
      void api
        .setIntegrationEnabled(id, enabled)
        .then((updated) => {
          if (updated) setHubIntegrations((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
          pushToast({ tone: 'success', message: `${integration.name} ${enabled ? 'enabled' : 'turned off'}.` });
        })
        .catch(() => {
          setHubIntegrations((prev) => prev.map((i) => (i.id === id ? { ...i, enabled: !enabled } : i)));
          setIntegrationEnablement((m) => ({ ...(m ?? {}), [id]: !enabled }));
          pushToast({ tone: 'error', message: `Couldn't update ${integration.name}.` });
        });
    },
    [api, hubIntegrations, pushToast, setHubIntegrations],
  );

  return {
    integrationEnablement,
    setIntegrationEnablement,
    runningIntegrations,
    onRunIntegration,
    onToggleIntegration,
  };
}
